import { useTranslation } from 'react-i18next'
import { Link, useParams, useSearchParams } from 'react-router'
import moment from 'moment'
import clsx from 'clsx'
import { Base, ReverLoading } from '@itsrever/design-system'
import { useSizingModel, useSizingStore } from '@/application/queries'
import { SizingDirection } from '@/domain/sizing'
import { formatCountries, formatCurrency } from '@/utils'
import { formatPercent } from '@/utils/format'
import ErrorNotFound from '@/ui/pages/404.page'
import {
  ProgressBar,
  ProgressLabel,
  BarPair,
  StatusPill
} from '@/ui/components/sizing/primitives'

const { Card } = Base

function DeltaBadge({ deltaPp }: { deltaPp: number | null }) {
  const { i18n } = useTranslation()
  if (deltaPp === null)
    return <span className="text-xs text-neutral-500">—</span>
  const isGood = deltaPp < 0
  const isFlat = Math.abs(deltaPp) < 0.5
  return (
    <span
      className={clsx(
        'ml-auto text-xs font-semibold tabular-nums',
        isFlat
          ? 'text-neutral-500'
          : isGood
            ? 'text-success-dark'
            : 'text-error-dark'
      )}
    >
      {deltaPp > 0 ? '+' : ''}
      {deltaPp.toLocaleString(i18n.language, { maximumFractionDigits: 1 })} pp
    </span>
  )
}

function DirectionBlock({
  title,
  hint,
  direction,
  highlightMax,
  bold
}: {
  title: string
  hint?: string
  direction: SizingDirection
  highlightMax: number
  bold?: boolean
}) {
  const { t, i18n } = useTranslation()
  const pct = (value: number | null) =>
    value === null ? '—' : formatPercent(value, i18n.language)
  return (
    <div className="border-0 border-b border-solid border-neutral-200 py-2.5 last:border-b-0">
      <div className="mb-1.5 flex items-baseline gap-2 text-sm">
        <span className={bold ? 'font-semibold' : 'font-medium'}>{title}</span>
        {hint ? <span className="text-xs text-neutral-500">{hint}</span> : null}
        <DeltaBadge deltaPp={direction.deltaPp} />
      </div>
      <BarPair
        label={t('sizing.detail.before')}
        pct={direction.rateBeforePct}
        maxPct={highlightMax}
        highlight={false}
        valueLabel={pct(direction.rateBeforePct)}
      />
      <BarPair
        label={t('sizing.detail.after')}
        pct={direction.rateAfterPct}
        maxPct={highlightMax}
        highlight
        valueLabel={pct(direction.rateAfterPct)}
      />
    </div>
  )
}

const SizingModelPage = () => {
  const { t, i18n } = useTranslation()
  const { id: modelKey } = useParams()
  const [searchParams] = useSearchParams()
  const { storeId, settingsLoaded } = useSizingStore()

  const cycle = searchParams.get('cycle')
  const { data: model, status } = useSizingModel(
    storeId,
    modelKey,
    cycle ?? undefined
  )

  // Without a storeId the query is skipped and never leaves 'pending'.
  if (settingsLoaded && !storeId) {
    return <ErrorNotFound />
  }
  if (!settingsLoaded || status === 'pending') {
    return (
      <div className="flex h-full items-center justify-center">
        <ReverLoading />
      </div>
    )
  }
  if (status === 'error' || !model) {
    return <ErrorNotFound />
  }

  const maxPct = Math.max(
    model.target.rateBeforePct ?? 0,
    model.target.rateAfterPct ?? 0,
    model.opposite.rateBeforePct ?? 0,
    model.opposite.rateAfterPct ?? 0,
    model.total.rateBeforePct ?? 0,
    model.total.rateAfterPct ?? 0,
    0.001
  )
  const targetIsSmall = model.sizeProblem === 'TOO_SMALL'
  const formatPp = (value: number | null) =>
    value === null
      ? '—'
      : `${value > 0 ? '+' : ''}${value.toLocaleString(i18n.language, {
          maximumFractionDigits: 1
        })} pp`

  return (
    <div className="flex h-full flex-col overflow-y-auto p-4">
      <Link
        to={`/size-suggestions?tab=models${cycle ? `&cycle=${cycle}` : ''}`}
        className="mb-3 text-sm font-semibold text-primary-base no-underline"
      >
        ← {t('sizing.detail.back')}
      </Link>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{model.modelName}</h1>
        <StatusPill status={model.status} />
        <span className="text-xs font-semibold tracking-wide text-neutral-600">
          {model.sizeProblem === 'TOO_BIG'
            ? t('sizing.models.direction_big')
            : t('sizing.models.direction_small')}
          {model.country !== 'WW'
            ? ` · ${formatCountries(model.country, i18n.language)}`
            : ''}
        </span>
        {model.bannerDate ? (
          <Base.Badge variant="outline" className="ml-auto">
            {t('sizing.detail.active_since', {
              date: moment(model.bannerDate).locale(i18n.language).format('ll')
            })}
          </Base.Badge>
        ) : null}
      </div>

      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="flex flex-col gap-3 self-start">
          <Card className="h-fit p-4">
            <h3 className="text-base font-semibold">
              {t('sizing.detail.returns_title')}
            </h3>
            <p className="mb-1 text-xs text-neutral-500">
              {t('sizing.detail.returns_sub', {
                units: (model.unitsAfter ?? 0).toLocaleString(i18n.language)
              })}
            </p>
            {model.status === 'COLLECTING_DATA' ? (
              <div className="py-3">
                <ProgressLabel progress={model.progress} />
                <ProgressBar
                  value={model.progress.units}
                  target={model.progress.unitsTarget}
                />
              </div>
            ) : (
              <>
                <p className="mb-2 flex gap-4 text-xs text-neutral-500">
                  <span>
                    <i className="mr-1.5 inline-block size-2.5 rounded-sm bg-neutral-300 align-[-1px]" />
                    {t('sizing.detail.legend_before')}
                  </span>
                  <span>
                    <i className="mr-1.5 inline-block size-2.5 rounded-sm bg-primary-base align-[-1px]" />
                    {t('sizing.detail.legend_after')}
                  </span>
                </p>
                <DirectionBlock
                  title={
                    targetIsSmall
                      ? t('sizing.detail.target_small')
                      : t('sizing.detail.target_big')
                  }
                  hint={t('sizing.detail.target_hint')}
                  direction={model.target}
                  highlightMax={maxPct}
                />
                <DirectionBlock
                  title={
                    targetIsSmall
                      ? t('sizing.detail.target_big')
                      : t('sizing.detail.target_small')
                  }
                  hint={t('sizing.detail.opposite_hint')}
                  direction={model.opposite}
                  highlightMax={maxPct}
                />
                <DirectionBlock
                  title={t('sizing.detail.total_label')}
                  direction={model.total}
                  highlightMax={maxPct}
                  bold
                />
                {model.status === 'WORKING' &&
                model.target.deltaPp !== null &&
                model.savedEurToDate !== null ? (
                  <p className="mt-3 rounded-lg border border-solid border-neutral-200 bg-neutral-100 px-3 py-2.5 text-sm">
                    {t('sizing.detail.net_line', {
                      gain: formatPp(model.target.deltaPp),
                      leak: formatPp(model.opposite.deltaPp),
                      net: formatPp(model.total.deltaPp),
                      savings: formatCurrency(
                        model.savedEurToDate.toFixed(0),
                        'EUR',
                        { lang: i18n.language, maximumFractionDigits: 0 }
                      )
                    })}
                  </p>
                ) : null}
              </>
            )}
          </Card>

          {model.quotes.length > 0 ? (
            <Card className="h-fit p-4">
              <h3 className="text-base font-semibold">
                {t('sizing.detail.quotes_title')}
              </h3>
              <p className="mb-2 text-xs text-neutral-500">
                {t('sizing.detail.quotes_sub')}
              </p>
              {model.quotes.map((quote) => (
                <p
                  key={quote}
                  className="my-2 border-0 border-l-2 border-solid border-neutral-200 pl-3 text-sm italic text-neutral-500"
                >
                  «{quote}»
                </p>
              ))}
            </Card>
          ) : null}
        </div>

        <Card className="h-fit p-4">
          <h3 className="text-base font-semibold">
            {t('sizing.detail.products_title')}
          </h3>
          <p className="mb-2 text-xs text-neutral-500">
            {t('sizing.detail.products_sub')}
          </p>
          {model.colorways.map((colorway) => (
            <div
              key={colorway.productId}
              className="flex items-center gap-3 border-0 border-b border-solid border-neutral-200 py-2 text-sm last:border-b-0"
            >
              {colorway.imageUrl ? (
                <img
                  src={colorway.imageUrl}
                  alt=""
                  className="h-10 w-8 flex-none rounded-md border border-solid border-neutral-200 object-cover"
                  // Deleted products lose their CDN file; fall back to the empty swatch.
                  onError={(event) => {
                    event.currentTarget.style.visibility = 'hidden'
                  }}
                />
              ) : (
                <span className="h-10 w-8 flex-none rounded-md border border-solid border-neutral-200 bg-neutral-100" />
              )}
              <span className="flex min-w-0 flex-col">
                <span className="font-semibold">{colorway.name}</span>
                <span className="text-xs text-neutral-400">
                  Product ID: {colorway.productId}
                </span>
              </span>
              <span className="ml-auto text-xs tabular-nums text-neutral-500">
                {t('sizing.detail.units', {
                  n: colorway.units.toLocaleString(i18n.language)
                })}
              </span>
            </div>
          ))}
          <p className="mt-2 text-xs text-neutral-500">
            {model.activeFrom || model.bannerDate
              ? t('sizing.detail.units_since', {
                  date: moment(model.activeFrom || model.bannerDate)
                    .locale(i18n.language)
                    .format('ll')
                })
              : null}{' '}
            {t('sizing.detail.colorway_note')}
          </p>
        </Card>
      </div>
    </div>
  )
}

export default SizingModelPage
